import { Fragment, useState } from "react"
import { useQuery } from "react-query"

import { Menu, Tab, Tabs } from "@synapse-analytics/synapse-ui"

import { VisionAPI } from "../../../API/VisionAPI"
import { definitions } from "../../../types/Generated/apiTypes"
import BranchesTab from "./BranchesTab/BranchesTab"
import CollectionsTab from "./CollectionsTab/CollectionsTab"
import AddNewCollectionDialog from "./components/AddNewCollectionDialog"

import styles from "./BranchesAndCollectionsMenu.module.scss"

type Branch = definitions["Branch"]

/**
 * Props for the BranchesAndCollectionsMenu component.
 */
interface BranchesAndCollectionsMenuProps {
  /** Whether the menu is currently open */
  open: boolean
  /** Callback invoked when the menu should be closed */
  onClose: () => void
  /** The HTML element to anchor the menu to */
  anchorEl: HTMLElement | null
}

/**
 * BranchesAndCollectionsMenu component provides a menu interface for managing branches and collections.
 *
 * Features:
 * - Tabbed interface with "All Branches" and "Collections" tabs
 * - Manages the "Add New Collection" dialog state
 * - Lazy loads uncollected branches when needed
 * - Provides a unified interface for branch selection and collection management
 *
 * @param props - The component props
 * @returns The rendered menu component
 */
const BranchesAndCollectionsMenu = ({ open, onClose, anchorEl }: BranchesAndCollectionsMenuProps) => {
  const [selectedTab, setSelectedTab] = useState<"branches" | "collections">("branches")
  const [isAddNewCollectionDialogOpen, setIsAddNewCollectionDialogOpen] = useState(false)

  const { data: uncollectedBranches, isLoading: areUncollectedBranchesLoading } = useQuery<Branch[]>(
    "fetchUncollectedBranches",
    () => VisionAPI.fetchBranches({ has_collection: false }),
    {
      enabled: selectedTab === "collections" || isAddNewCollectionDialogOpen,
    }
  )

  return (
    <Fragment>
      <Menu open={open} onClose={onClose} anchorEl={anchorEl} className={styles.menu} menuMaxContent minHeight={378}>
        <Tabs activeTab={selectedTab} className={styles.tabs}>
          <Tab
            label="All Branches"
            value="branches"
            selected={selectedTab === "branches"}
            onClick={() => setSelectedTab("branches")}
            className={styles.tab}
          />
          <Tab
            label="Collections"
            value="collections"
            selected={selectedTab === "collections"}
            onClick={() => setSelectedTab("collections")}
            className={styles.tab}
          />
        </Tabs>
        {selectedTab === "branches" ? (
          <BranchesTab
            onAddNewCollection={() => {
              setIsAddNewCollectionDialogOpen(true)
              onClose()
            }}
          />
        ) : (
          <CollectionsTab
            branches={uncollectedBranches}
            onAddNewCollection={() => {
              setIsAddNewCollectionDialogOpen(true)
              onClose()
            }}
            areBranchesLoading={areUncollectedBranchesLoading}
          />
        )}
      </Menu>
      <AddNewCollectionDialog
        onClose={() => setIsAddNewCollectionDialogOpen(false)}
        isOpen={isAddNewCollectionDialogOpen}
        branches={uncollectedBranches}
      />
    </Fragment>
  )
}
export default BranchesAndCollectionsMenu
